import { useLazyQuery, useMutation } from "@apollo/client";

import {
  CreateResourcePermissionsDocument,
  CreateResourcePermissionsInput,
  CreateResourcePermissionsMutation,
  CreateResourcePermissionsMutationVariables,
  ResourcePermissionsDocument,
  ResourcePermissionsQuery,
  ResourcePermissionsQueryVariables,
  ResourceType,
  UpdateResourcePermissionsDocument,
  UpdateResourcePermissionsInput,
  UpdateResourcePermissionsMutation,
  UpdateResourcePermissionsMutationVariables,
} from "@/graphql";

export const useResourcePermissionsQuery = ({
  onCompleted,
  onError,
}: {
  onCompleted?: (data: ResourcePermissionsQuery["resourcePermissions"]) => void;
  onError?: (error: Error) => void;
}) => {
  const [fetch, { data, loading }] = useLazyQuery<
    ResourcePermissionsQuery,
    ResourcePermissionsQueryVariables
  >(ResourcePermissionsDocument, {
    fetchPolicy: "network-only",
    onCompleted: (response) => {
      if (response?.resourcePermissions && onCompleted) {
        onCompleted(response.resourcePermissions);
      }
    },
    onError: (err) => {
      if (onError) onError(err);
    },
  });

  return {
    resourcePermissions: data?.resourcePermissions || [],
    loading,
    fetchResourcePermissions: (
      resourceType: ResourceType,
      resourceId: string,
    ) => fetch({ variables: { resourceType, resourceId } }),
  };
};

export const useCreateResourcePermissionsMutation = ({
  onCompleted,
  onError,
  refetchQueries = null,
}: {
  onCompleted?: (
    data: CreateResourcePermissionsMutation["createResourcePermissions"],
  ) => void;
  onError?: (error: string) => void;
  refetchQueries?: any;
}) => {
  const [createResourcePermissions, { loading }] = useMutation<
    CreateResourcePermissionsMutation,
    CreateResourcePermissionsMutationVariables
  >(CreateResourcePermissionsDocument, {
    onCompleted: (response) => {
      if (response?.createResourcePermissions?.ok && onCompleted) {
        onCompleted(response.createResourcePermissions);
      } else if (!response?.createResourcePermissions?.ok && onError) {
        const errors = response?.createResourcePermissions?.errors || [];
        const errMsg = errors.map((e) => e.messages?.join(", ")).join("; ");
        onError(
          errMsg || "An error occurred while creating resource permissions",
        );
      }
    },
    onError: (err) => {
      if (onError)
        onError(
          err.message ||
            "An error occurred while creating resource permissions",
        );
    },
    refetchQueries,
  });

  return {
    createResourcePermissions: (input: CreateResourcePermissionsInput) =>
      createResourcePermissions({
        variables: { input },
      }),
    loading,
  };
};

export const useUpdateResourcePermissionsMutation = ({
  onCompleted,
  onError,
  refetchQueries = null,
}: {
  onCompleted?: (
    data: UpdateResourcePermissionsMutation["updateResourcePermissions"],
  ) => void;
  onError?: (error: string) => void;
  refetchQueries?: any;
}) => {
  const [updateResourcePermissions, { loading }] = useMutation<
    UpdateResourcePermissionsMutation,
    UpdateResourcePermissionsMutationVariables
  >(UpdateResourcePermissionsDocument, {
    onCompleted: (response) => {
      if (response?.updateResourcePermissions?.ok && onCompleted) {
        onCompleted(response.updateResourcePermissions);
      } else if (!response?.updateResourcePermissions?.ok && onError) {
        const errors = response?.updateResourcePermissions?.errors || [];
        const errMsg = errors.map((e) => e.messages?.join(", ")).join("; ");
        onError(
          errMsg || "An error occurred while updating resource permissions",
        );
      }
    },
    onError: (err) => {
      if (onError)
        onError(
          err.message ||
            "An error occurred while updating resource permissions",
        );
    },
    refetchQueries,
  });

  return {
    updateResourcePermissions: (input: UpdateResourcePermissionsInput) =>
      updateResourcePermissions({
        variables: { input },
      }),
    loading,
  };
};
